import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { JSX, MouseEvent, ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import {
  BookOpen, Building2, Banknote, ChevronLeft, ChevronRight, ClipboardCheck, LayoutDashboard,
  Package, Percent, Settings, ShoppingCart, Target, Truck,
} from 'lucide-react'
import { toBlocks, visibleBottom, visibleLeaves, type Leaf } from './nav.ts'
import { tDynamic } from './i18n/index.ts'
import type { CostViewFlags } from '@rs/policy/routes'

/** Nhãn mục / thư mục con của sidebar (`nav.<khoá>`) — MỘT chỗ gọi `tDynamic` cho cả mục lá, thư mục con và bảng nổi khi thu gọn. */
const navLabel = (t: TFunction, key: string): string => tDynamic(t, `nav.${key}`)

/**
 * ══ KHUNG ỨNG DỤNG — sidebar LOGISTICS RT ══════════════════════════════════════════════════════
 * ⚠ KHÔNG có dropdown chọn pháp nhân: chỉ còn MỘT pháp nhân thì không có gì để chọn (CLAUDE.md §5).
 *
 * ⚠ NHÃN LẤY TỪ i18next THẬT — **THIẾT KẾ B** (Task 21) ═════════════════════════════════════════
 * Task 19 nhận nhãn qua prop `t` (bảng cứng `i18n-shim.ts`) vì i18next chưa dựng. Nay `useTranslation()`
 * đọc thẳng kho chuỗi 3 ngôn ngữ.
 *
 * ⚠ Khoá là `nav.<x>` / `common.<x>` — **CÓ DẤU CHẤM, MỘT namespace `translation`**, KHÔNG phải
 *   `nav:<x>`. Xem khối chú thích đầu `src/i18n/index.ts`; đấu theo `ns:key` là mọi nhãn thành khoá
 *   thô. Khoá dựng động (`nav.${item.key}` · `roles.${me.role}`) đi qua cửa `tDynamic()` — bản cũ làm
 *   y hệt (`Layout.tsx:274,798`).
 */
export interface AppLayoutProps {
  /**
   * ⚠ `azSeeVendorCost` + `azSalesScope` là BẮT BUỘC, KHÔNG phải tuỳ chọn (C10).
   * `GET /api/v1/auth/me` trả thẳng `req.actor` (`SessionUser`) nên hai trường này LUÔN có.
   * Để tuỳ chọn thì một lời gọi quên truyền sẽ ẩn oan hai màn giá vốn của `sale` được tick —
   * đúng loại lệch giữa menu và chốt chặn mà Task 18 sinh ra để xoá.
   */
  me: { name: string; role: string; azSeeVendorCost: boolean; azSalesScope: 'own' | 'all' }
  /** Đường dẫn ĐANG ĐỨNG — quyết định thư mục con nào tự bung (xem `NavSection`). */
  currentPath?: string
  languagePicker: ReactNode
  /**
   * ⭐ CHUÔNG THÔNG BÁO (`0070`) — TUỲ CHỌN, và truyền vào từ `apps/web` chứ không dựng ở đây:
   *   `@rs/ui` không gọi máy chủ, không biết `qk`, và không được biết. Cùng khuôn `languagePicker`.
   */
  notifBell?: ReactNode
  /**
   * Nút tài khoản cạnh "Đăng xuất" — `apps/web` truyền nút 🔒 Mật khẩu (E13 sửa 19/10/2026). TUỲ CHỌN và truyền từ ngoài,
   * cùng khuôn `notifBell`: `@rs/ui` không gọi máy chủ.
   */
  accountAction?: ReactNode
  children: ReactNode
  onNavigate?: (to: string) => void
  onLogout?: () => Promise<void> | void
}

/**
 * ══ ICON TRƯỚC MỖI MỤC MENU — chép bảng `ICON` của `Layout.tsx:71-111` (G3) ═════════════════════
 *
 * ⚠ CHỈ 10 ICON, KHÔNG PHẢI 18 — và con số đó không phải do cắt bớt:
 *   · Bản cũ **BỎ ICON cho mục nằm trong thư mục con** (`Layout.tsx:245`, user bắt sửa 14/07:
 *     *"3 mục con lệch hàng nhau"* khi chỉ một số mục có icon). Phạm vi RT có 8 mục nằm trong hai
 *     thư mục Bán hàng · Vận hành ⇒ 8 mục đó KHÔNG cần icon.
 *   · Còn lại: 7 mục lẻ + 2 tiêu đề thư mục + 1 tiêu đề nhóm = **10**.
 *
 * ⚠ IMPORT LẺ TỪNG ICON, KHÔNG `import * as icons`. `lucide-react` có hơn 1.500 icon; kéo cả gói
 *   vào là vỡ cổng `ci:budget` (gói khởi tạo chỉ còn ~10 kB dư địa) vì đúng một thanh điều hướng.
 *
 * Ba icon của bản cũ KHÔNG có ở đây vì mục tương ứng nằm trong thư mục con: `Handshake` (Đơn hàng)
 * · `ReceiptText` (Chứng từ BH) · `Snowflake` (Vỏ lạnh). Muốn hiện thì phải bỏ luật 14/07 trước.
 */
const ICON: Record<string, typeof Package> = {
  // — tiêu đề nhóm + hai tiêu đề thư mục con —
  az: Package,
  azSalesGrp: ShoppingCart,
  azOpsGrp: Truck,
  // — bảy mục lẻ —
  azDashboard: LayoutDashboard,
  azApprovals: ClipboardCheck,
  azDisburse: Banknote,
  quanTri: Building2,
  azCatalog: BookOpen,
  settings: Settings,
  /** ⚠ HAI MỤC KHÔNG CÓ GỐC trong bảng `ICON` bản cũ — `/az/hoa-hong` (F27) và `/az/ke-hoach`
   *  (F31) là mục menu rs-erp thêm (xem `scripts/ui-parity/routes.ts`). Không có icon nào để chép,
   *  nên chọn theo NGHĨA và ghi rõ ở đây: hoa hồng = phần trăm, kế hoạch = mục tiêu. Nếu anh chốt
   *  gộp hai màn này vào chỗ khác thì xoá luôn hai dòng này. */
  azCommission: Percent,
  azPlan: Target,
}

interface LeafProps {
  item: Leaf
  t: TFunction
  indented?: boolean
  currentPath: string
  onNavigate?: ((to: string) => void) | undefined
  /** Sidebar đang rộng hay đã thu về `w-14`. Thu gọn thì chỉ còn icon + tooltip khi rê chuột. */
  expanded?: boolean
}

/** Mục đang đứng — khớp CHÍNH NÓ hoặc nhánh con của nó (`/az/ops` khớp `/az/ops/123`), không khớp
 *  nửa chừng (`/ap` KHÔNG nuốt `/approvals`) — cùng quy tắc biên với `canAccessRoute`. */
const isCurrent = (currentPath: string, to: string): boolean =>
  currentPath === to || currentPath.startsWith(`${to}/`)

/**
 * ══ MENU CHUỘT PHẢI TRÊN MỤC MENU — chép `Layout.tsx:302-320` (user chốt 30/08) ═════════════════
 *
 * ⚠ ĐÂY KHÔNG PHẢI TIỆN ÍCH THÊM CHO VUI. `NavLeaf` chặn cú click thường bằng `preventDefault()`
 * để điều hướng trong ứng dụng, nên thói quen "mở màn này ra tab riêng để đối chiếu hai màn" —
 * việc kế toán làm suốt — mất đường. Bản cũ dựng hẳn menu này để trả lại đường đó, và chú thích
 * trong `NavLeaf` đã ghi rõ là mở tab mới *"được phục vụ CÓ CHỦ ĐÍCH"*.
 *
 * ⚠ Ctrl/Cmd/Shift/Alt+click và click chuột giữa vẫn đi đường của trình duyệt — xem `NavLeaf`.
 *   Menu này là đường thứ hai, không thay đường đó.
 *
 * ⚠ CHUỖI GÕ CỨNG, không qua kho chuỗi — CỐ Ý, và giống bản cũ từng ký tự (`Layout.tsx:312,316`).
 *   Bản cũ cũng gõ cứng hai nhãn này. Thêm khoá i18n mới đòi đủ 3 ngôn ngữ (§9) và phải có người
 *   duyệt câu chữ; đó là việc của anh, không phải việc lén thêm trong lúc port.
 */
const useLinkCtxMenu = (onNavigate?: (to: string) => void): {
  openCtx: (url: string) => (e: MouseEvent<HTMLElement>) => void
  menu: JSX.Element | null
} => {
  const [ctx, setCtx] = useState<{ x: number; y: number; url: string } | null>(null)
  const openCtx = (url: string) => (e: MouseEvent<HTMLElement>): void => {
    e.preventDefault(); e.stopPropagation()
    setCtx({ x: e.clientX, y: e.clientY, url })
  }
  const menu = ctx === null ? null : createPortal(
    <>
      <div
        className="fixed inset-0 z-[60]"
        onClick={() => { setCtx(null) }}
        onContextMenu={(e) => { e.preventDefault(); setCtx(null) }}
      />
      <div className="fixed z-[61] bg-white border border-slate-200 rounded-lg shadow-lg py-1 text-xs" style={{ left: ctx.x, top: ctx.y }}>
        <button
          type="button" className="block w-full text-left px-3 py-1.5 hover:bg-sky-50 text-slate-700 whitespace-nowrap"
          onClick={() => { window.open(ctx.url, '_blank', 'noopener'); setCtx(null) }}
        >
          ↗ Mở trong tab mới
        </button>
        <button
          type="button" className="block w-full text-left px-3 py-1.5 hover:bg-sky-50 text-slate-700 whitespace-nowrap"
          onClick={() => { onNavigate?.(ctx.url); setCtx(null) }}
        >
          → Mở tại trang này
        </button>
      </div>
    </>, document.body,
  )
  return { openCtx, menu }
}

const NavLeaf = ({ item, t, indented, currentPath, onNavigate, expanded = true }: LeafProps): JSX.Element => {
  const cur = isCurrent(currentPath, item.to)
  const Icon = ICON[item.key]
  // `compact` = sidebar đã thu, và CHỈ có nghĩa trên desktop (`md:`) — màn hẹp dùng drawer, luôn rộng.
  const compact = !expanded
  const { openCtx, menu } = useLinkCtxMenu(onNavigate)
  return (
  <li className="relative group">
    <a
      href={item.to}
      // S1 — phần NGỮ NGHĨA của "mục đang mở" (bản cũ tô màu bằng `NavLink` isActive).
      aria-current={cur ? 'page' : undefined}
      onContextMenu={openCtx(item.to)}
      // Tầng thiết kế — chép NGUYÊN VĂN `Layout.tsx:260-268` (C12). Bản cũ lấy `isActive` từ
      // `<NavLink>`; ở đây `cur` đã tính sẵn cho `aria-current`, dùng lại chính nó nên màu và
      // ngữ nghĩa KHÔNG THỂ lệch nhau. Bỏ nhánh `compact` của bản cũ vì `rs-erp` chưa có nút
      // thu gọn sidebar (nợ §7 đã ghi ở biên bản, không bịa thêm ở đây).
      className={[
        'relative flex items-center gap-3 transition rounded-lg',
        indented === true ? 'px-3 py-2 text-[13px]' : 'px-3 py-2.5 text-sm',
        // Thu gọn: ô vuông 40px căn giữa, bỏ chữ — chép `Layout.tsx:250`.
        compact ? 'md:justify-center md:w-10 md:h-10 md:mx-auto md:px-0 md:py-0' : '',
        cur
          ? 'bg-brand-50 text-brand-600 font-bold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-r-full before:bg-brand-500 before:block'
          : indented === true
            ? 'text-slate-500 hover:bg-slate-50 hover:text-brand-600 font-medium'
            : 'text-slate-700 hover:bg-slate-50 hover:text-brand-600 font-bold',
      ].join(' ')}
      onClick={(e) => {
        if (!onNavigate) return
        /* ⚠ KHÔNG CƯỚP Ctrl/Cmd/Shift/Alt+click và click chuột giữa ═════════════════════════════
           Bản cũ dùng `<NavLink>` của react-router, nó tự bỏ qua các cú click đó (`isModifiedEvent`)
           để trình duyệt mở tab/cửa sổ mới như thường. Bản cũ còn dựng HẲN một menu chuột phải
           "↗ Mở trong tab mới" (`Layout.tsx:302-320`, user chốt 30/08) — tức mở tab mới là thói
           quen ĐƯỢC PHỤC VỤ CÓ CHỦ ĐÍCH, không phải tình cờ. `preventDefault()` vô điều kiện sẽ
           nuốt cú click đó và điều hướng NGAY TRONG tab đang mở — mất trang người dùng đang xem. */
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
        e.preventDefault()
        onNavigate(item.to)
      }}
    >
      {/* Icon CHỈ cho mục đứng độc lập — mục trong thư mục con bỏ icon và thụt vào đều, đúng
          `Layout.tsx:245` (user 14/07). `strokeWidth` dày lên khi đang đứng ở mục đó, y bản cũ. */}
      {indented !== true && Icon !== undefined && (
        <Icon size={19} strokeWidth={cur ? 2 : 1.6} className="shrink-0" />
      )}
      <span className={compact ? 'md:hidden' : ''}>{navLabel(t, item.key)}</span>
    </a>
    {/* Tooltip khi thu gọn — chép `Layout.tsx:278-282`. Không có nó thì sidebar hẹp chỉ còn 10 icon
        không nhãn, và không ai nhớ được icon nào là màn nào. */}
    {compact && (
      <div className="pointer-events-none hidden md:flex items-center absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1 bg-slate-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
        {navLabel(t, item.key)}
      </div>
    )}
    {menu}
  </li>
  )
}

/**
 * THƯ MỤC CON trong nhóm (Bán hàng ▸ Đơn hàng · Chứng từ BH · Giá công bố · Thống kê) —
 * chép `Layout.tsx:305-338` (`NavSection`):
 *  · TỰ BUNG khi đang đứng trong một mục của nó, TỰ KHÉP khi rời đi (user 14/07 + sửa lần 2 22/08:
 *    *"chuyển sang menu khác mà nó không tự khép"*).
 *  · Bấm THẲNG vào tiêu đề "Bán hàng"/"Vận hành" thì NHẢY LUÔN vào mục con ĐẦU TIÊN và bung ra
 *    (user 22/08) — CHỈ áp cho đúng hai thư mục này, và đó cũng là hai thư mục duy nhất còn lại
 *    trong phạm vi RT, nên không cần cờ `navFirstChild` riêng.
 */
const NavSection = ({
  section,
  items,
  t,
  currentPath,
  onNavigate,
  expanded = true,
}: {
  section: string
  items: Leaf[]
  t: TFunction
  currentPath: string
  onNavigate?: ((to: string) => void) | undefined
  expanded?: boolean
}): JSX.Element => {
  const inside = items.some((i) => isCurrent(currentPath, i.to))
  const SectionIcon = ICON[section]
  const [open, setOpen] = useState(inside)
  // ⚠ Bản cũ đồng bộ chỗ này bằng `useEffect(() => setOpen(inside), [inside])` (`Layout.tsx:322`).
  //   KHÔNG bê sang: đó đúng họ 102 lỗi hook của bản cũ mà quyết định **B1** (anh chốt 05/09) đã
  //   chốt là không mang theo, và luật lint `react-hooks/set-state-in-effect` chặn thật.
  //   Thay bằng khuôn CHÍNH THỨC của React cho "chỉnh state khi prop đổi" (You Might Not Need an
  //   Effect) — HÀNH VI GIỮ NGUYÊN 100%: tự bung khi bước vào thư mục, tự khép khi rời đi.
  const [prevPath, setPrevPath] = useState(currentPath)
  if (currentPath !== prevPath) {
    setPrevPath(currentPath)
    setOpen(inside)
  }
  const first = items[0]

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          if (first) onNavigate?.(first.to)
          setOpen(true)
        }}
        // Chép NGUYÊN VĂN `Layout.tsx:326`.
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-50 hover:text-brand-600 transition"
      >
        {SectionIcon !== undefined && <SectionIcon size={19} strokeWidth={inside ? 2 : 1.6} className="shrink-0" />}
        <span className={expanded ? '' : 'md:hidden'}>{navLabel(t, section)}</span>
        {/* Mũi tên xoay 90° khi bung — `Layout.tsx:329`.
            ⚠ `aria-hidden` BẮT BUỘC: không có nó, ký tự ▶ nhập vào TÊN KHẢ TRUY CẬP của nút
            ("Bán hàng ▶") và 5 ca test tìm nút theo tên đứt ngay — trình đọc màn hình cũng đọc
            "Bán hàng hình tam giác đen chỉ sang phải". Trạng thái bung/khép đã nói bằng
            `aria-expanded` ở trên, nên glyph này thuần trang trí. Không đổi một pixel nào. */}
        <span aria-hidden="true" className={`ml-auto text-[9px] transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
      </button>
      {/* Vạch trái + thụt để thấy rõ thư mục con KẾT THÚC ở đâu (`Layout.tsx:334`, user 14/07:
          không có nó thì Vận hành/Duyệt chi bên dưới bị nhìn như cũng là con của Bán hàng). */}
      {open && (
        <ul className="ml-2 pl-2 border-l border-slate-200 space-y-0.5">
          {items.map((i) => (
            <NavLeaf key={i.to} item={i} t={t} indented currentPath={currentPath} onNavigate={onNavigate} expanded={expanded} />
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * ══ SIDEBAR THU GỌN: MỘT ICON NHÓM + FLYOUT — chép `NavGroup` nhánh "Desktop compact" (`Layout.tsx:447-500`) ══
 *
 * Bản cũ thu gọn thì cả nhóm "LOGISTICS RT" chỉ còn MỘT icon; bấm vào bung một bảng nổi bên phải
 * liệt kê PHẲNG mọi mục con (tiêu đề thư mục con là nhãn nhỏ, không sổ/thu). Trước đây rs-erp vẫn
 * vẽ từng mục con dạng ô 40px — mà mục trong thư mục con KHÔNG có icon (luật 14/07) ⇒ ô vuông rỗng,
 * không bấm ra gì để biết (soát 16/10, T2: `t2-collapsed-new.png`).
 *
 * ⚠ Bảng nổi đi qua PORTAL + `position: fixed` — y bản cũ: sidebar có `overflow-y-auto` nên một khối
 *   `absolute` trong nó bị cắt mất, không hiện ra được.
 * ⚠ Bấm ra ngoài thì đóng (bản cũ mở bằng click chứ không chỉ rê chuột, nên phải có lối đóng).
 */
const CompactGroup = ({
  leaves, t, currentPath, onNavigate,
}: {
  leaves: Leaf[]
  t: TFunction
  currentPath: string
  onNavigate: (to: string) => void
}): JSX.Element => {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: globalThis.MouseEvent): void => {
      const target = e.target as Node
      if (btnRef.current?.contains(target) === true) return
      if (menuRef.current !== null && !menuRef.current.contains(target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('mousedown', onDown) }
  }, [open])

  const toggle = (): void => {
    const r = btnRef.current?.getBoundingClientRect()
    if (r !== undefined) setPos({ top: r.top, left: r.right + 8 })
    setOpen((o) => !o)
  }

  return (
    <div className="hidden md:flex justify-center py-0.5 relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        title={t('nav.az')}
        aria-label={t('nav.az')}
        aria-expanded={open}
        className={`flex items-center justify-center w-10 h-10 rounded-xl transition ${
          open ? 'bg-brand-50 text-brand-600' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
        }`}
      >
        <Package size={20} strokeWidth={1.75} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="bg-white border border-slate-200 shadow-xl rounded-xl py-2 w-48 z-50 max-h-[80vh] overflow-y-auto"
        >
          <div className="px-3 pb-1.5 mb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-100">
            {t('nav.az')}
          </div>
          {leaves.map((c, i) => {
            // In phẳng theo khối: tiêu đề thư mục con là NHÃN (không sổ/thu — bảng nổi vốn đã nhỏ),
            // chỉ in ở mục ĐẦU của mỗi khối liền kề cùng thư mục.
            const caption = c.section !== undefined && c.section !== leaves[i - 1]?.section ? c.section : ''
            const CIcon = c.section === undefined ? ICON[c.key] : undefined
            const cur = isCurrent(currentPath, c.to)
            return (
              <div key={c.to}>
                {caption !== '' && (
                  <div className="px-3 pt-2 pb-0.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{navLabel(t, caption)}</div>
                )}
                <a
                  href={c.to}
                  role="menuitem"
                  aria-current={cur ? 'page' : undefined}
                  onClick={(e) => {
                    // Ctrl/Cmd/Shift/Alt+click, chuột giữa ⇒ để trình duyệt mở tab mới (xem `NavLeaf`).
                    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
                    e.preventDefault()
                    setOpen(false)
                    onNavigate(c.to)
                  }}
                  className={`flex items-center gap-2.5 px-3 py-2 text-sm transition ${c.section !== undefined ? 'pl-7' : ''} ${
                    cur ? 'text-brand-600 bg-brand-50 font-semibold' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {CIcon !== undefined && <CIcon size={15} strokeWidth={1.75} />}
                  {navLabel(t, c.key)}
                </a>
              </div>
            )
          })}
        </div>,
        document.body,
      )}
    </div>
  )
}

export const AppLayout = ({
  me,
  currentPath = '',
  languagePicker,
  notifBell,
  accountAction,
  children,
  onNavigate,
  onLogout,
}: AppLayoutProps): JSX.Element => {
  const { t } = useTranslation()
  // ⚠ Cờ giá vốn TỪNG NGƯỜI PHẢI đi cùng vai — xem `visibleLeaves` và C10.
  const flags: CostViewFlags = { azSeeVendorCost: me.azSeeVendorCost, azSalesScope: me.azSalesScope }
  const leaves = visibleLeaves(me.role, flags)
  const bottom = visibleBottom(me.role, flags)
  /* ── HAI TRẠNG THÁI VỎ, chép `Layout.tsx:595` và `:713` ────────────────────────────────────────
     · `expanded` — sidebar rộng `w-56` hay thu về `w-14` (CHỈ trên desktop). Bản cũ mặc định MỞ.
     · `drawer`   — sidebar có hiện không, trên màn hẹp. Bản cũ mặc định ĐÓNG, bấm ☰ để mở, và
       **tự đóng khi chọn xong một mục** — không có bước đó thì trên điện thoại menu che mất
       chính cái màn vừa bấm vào. */
  const [expanded, setExpanded] = useState(true)
  const [drawer, setDrawer] = useState(false)
  /**
   * Nhóm "LOGISTICS RT" bung hay khép — chép `NavGroup` (`Layout.tsx:361-367`), HAI trạng thái như
   * bản cũ (KH-06, soát 16/10):
   *  · màn hẹp (drawer): mặc định BUNG (`mobileOpen = useState(true)`);
   *  · desktop: BUNG khi đang đứng trong nhóm, TỰ KHÉP khi rời ra mục ngoài nhóm (Cài đặt…) —
   *    `desktopOpen = useState(inGroup)` + đồng bộ theo `inGroup` (user 22/08 "chuyển menu khác mà nó
   *    không tự khép"). Đồng bộ bằng khuôn "chỉnh state khi prop đổi" như `NavSection` (B1, không effect).
   */
  const inGroup = leaves.some((l) => isCurrent(currentPath, l.to))
  const [mobileOpen, setMobileOpen] = useState(true)
  const [desktopOpen, setDesktopOpen] = useState(inGroup)
  const [prevInGroup, setPrevInGroup] = useState(inGroup)
  if (inGroup !== prevInGroup) {
    setPrevInGroup(inGroup)
    setDesktopOpen(inGroup)
  }
  /* ⭐ T2 — đổi màn tại chỗ (không tải lại trang) thì vùng cuộn `<main>` GIỮ vị trí cũ: sang màn mới
     mà đang ở lưng chừng trang. Đưa về đầu mỗi khi đổi đường dẫn — như một lần mở trang. */
  const mainRef = useRef<HTMLElement>(null)
  useEffect(() => { mainRef.current?.scrollTo?.(0, 0) }, [currentPath])
  /** Bấm một mục: điều hướng rồi ĐÓNG drawer. Trên desktop `drawer` vốn không dùng nên vô hại. */
  const go = (to: string): void => { setDrawer(false); onNavigate?.(to) }

  return (
    /* ⚠ `h-full`, KHÔNG `h-screen` — chép `Layout.tsx:715`. `h-screen` = 100vh, mà trên Safari/Chrome
       điện thoại 100vh tính cả phần thanh địa chỉ che ⇒ đáy vùng nội dung bị khuất (soi mobile 12/10).
       `html, body, #root` đã `height: 100%` ở `index.css`. */
    <div className="h-full flex flex-col">
      <header
        // Dải cam→xanh, cao 14 — `Layout.tsx:717`, chép nguyên văn. Còn thiếu chuông thông báo
        // (`NotifBell`) và nút ☰ mở drawer di động — hai nợ §7 còn lại của vỏ, xem §C12.
        // ⚠ Lệch bản cũ dưới `sm`: khoảng cách, logo `h-8`, nút 🔒 chỉ còn biểu tượng — ở 390px cụm nút
        //   phải tràn, "Đăng xuất" bị cắt nửa (30/10). Từ `sm` trở lên vẫn y bản cũ.
        className="h-14 shrink-0 bg-gradient-to-r from-brand-500 to-sky-500 text-white flex items-center px-2 sm:px-3 gap-2 sm:gap-3 shadow"
      >
        {/* ⭐ NÚT ☰ MỞ DRAWER — chép `Layout.tsx:718-720`. CHỈ hiện dưới `md`; trên desktop sidebar
            luôn đứng nên nút này thừa. `aria-expanded` để trình đọc màn hình biết drawer đang mở. */}
        <button
          type="button"
          onClick={() => { setDrawer((v) => !v) }}
          aria-label="menu"
          aria-expanded={drawer}
          className="md:hidden text-xl leading-none px-1"
        >
          ☰
        </button>
        {/* ⭐ LOGO + TÊN CÔNG TY — chép `Layout.tsx:721-722`, class NGUYÊN VĂN.
            ⚠ HAI CHỖ CỐ Ý LỆCH BẢN CŨ, cả hai do anh chốt 11/09 (§C12 câu 1 và 2):
            ① Chữ là **"RATRACO Solutions"**, không phải "PROSHIP JSC". §7 KHÔNG áp được ở đây:
              rs-erp chỉ còn MỘT pháp nhân (CLAUDE.md §5) và nó không phải PROSHIP. Chép nguyên
              bản cũ là hiện sai tên công ty trên mọi màn. Đổi ở `locales/{vi,en,zh}/app.json`
              chứ không viết thẳng vào đây — tên công ty là chuỗi, phải đi qua kho chuỗi (§9).
            ② Ảnh là **`/RT-LOGO-header.jpg`**, không phải `/LOGO.PNG` của bản cũ (logo PROSHIP).
              Nguồn: `proship-trans/public/RT-LOGO.jpg` — chính tệp `Logo.pnj.jpg` anh chỉ định
              (trùng md5), và bản cũ cũng đã dùng nó với `alt="RATRACO Solutions"` trong
              `AzDashboard.tsx`. Bản gốc 6667×2349 px / 1,17 MB được giữ nguyên ở
              `public/RT-LOGO.jpg` cho G4 dashboard; ở đây dùng bản thu về 318×112 px / 14,8 KB.
              ⚠ KHÔNG dùng bản gốc cho header: ô này cao 40 px, mà header render ở MỌI màn ⇒ mỗi
              lượt mở app tải thừa 1,15 MB. Bản cũ không dính vì nó chỉ nạp RT-LOGO trong
              dashboard, không nạp ở header. */}
        <img
          src="/RT-LOGO-header.jpg"
          alt={t('app.name')}
          className="h-8 sm:h-10 w-auto shrink-0 object-contain bg-white rounded-lg px-2 py-1"
        />
        <span className="font-bold text-white text-base tracking-wide hidden sm:block">{t('app.name')}</span>
        <div className="ml-auto flex items-center gap-1 sm:gap-3">
          {notifBell}
          {languagePicker}
          {/* ⭐ P17 — HAI DÒNG: tên người + TÊN VAI, chép `Layout.tsx:796-799` bản cũ:
                <div className="text-sm font-semibold">{user?.name}</div>
                <div className="text-[11px] text-brand-50">{user && t(`roles.${user.role}`)}</div>
              Task 19 chỉ dựng dòng tên và không ai đối chiếu ra, vì kế hoạch Task 19 cũng viết
              thiếu đúng khối này. Dòng tên vai là thứ cho kế toán biết mình đang đăng nhập bằng
              quyền nào trước khi bấm duyệt.
              ⚠ `roles` VÌ THẾ PHẢI nằm trong `STARTUP_KEY_GROUPS` — bỏ ra là sidebar của MỌI vai
              hiện `roles.ke_toan`. Ca kiểm "tên vai hiện ra cho cả 8 vai" canh đúng chỗ này. */}
          <div className="text-right leading-tight hidden sm:block">
            <div className="text-sm font-semibold" data-testid="ten-nguoi">{me.name}</div>
            <div className="text-[11px] text-brand-50" data-testid="ten-vai">{tDynamic(t, `roles.${me.role}`)}</div>
          </div>
          {/* Đăng xuất HAI CHẶNG: huỷ phiên rs-erp trước, rồi mới sang trang đăng xuất của FADO SSO
              (xem `useAuth().logout`). Làm ngược lại thì phiên rs-erp còn sống trong lúc trình duyệt
              đang rời trang.
              `common.logout` — khoá CÓ SẴN của bản cũ (`i18n.ts:366` = 'Đăng xuất'), KHÔNG phải khoá mới.
              Class chép `Layout.tsx:800`. */}
          {accountAction}
          <button
            type="button"
            onClick={() => { void onLogout?.() }}
            className="text-xs font-semibold whitespace-nowrap px-2.5 sm:px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30"
          >
            {t('common.logout')}
          </button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
      {/* Nhãn vùng điều hướng dùng CHÍNH tên nhóm (`nav.az` = "LOGISTICS RT"). Cố ý KHÔNG bịa một
          khoá i18n mới kiểu `nav.sidebar`: bản cũ không có khoá đó, và mọi khoá mới phải thêm đủ
          3 ngôn ngữ (CLAUDE.md §9) — thêm khoá không có trong bản cũ là việc của người quyết, không
          phải của Task 19. */}
      {/* ⚠ HAI THẺ LỒNG NHAU (`aside` bọc `nav`) — đúng `Layout.tsx:809` + `:824`, KHÔNG gộp làm
          một như bản trước. `aside` giữ bề rộng + hiện/ẩn drawer; `nav` giữ nội dung và nhãn vùng.
          Gộp lại thì không chỗ nào đặt được cụm nút thu gọn nằm NGOÀI danh sách mục. */}
      <aside
        className={`${drawer ? 'block' : 'hidden'} md:block shrink-0 bg-white border-r border-slate-200 overflow-y-auto absolute md:static z-20 h-[calc(100%-3.5rem)] md:h-auto transition-all duration-200 ${
          expanded ? 'w-56 md:w-56' : 'w-56 md:w-14'
        }`}
      >
        {/* Nút thu gọn — `Layout.tsx:814-821`. Chỉ có trên desktop: màn hẹp đã có ☰. */}
        <div className={`hidden md:flex pt-2 pb-1 px-2 ${expanded ? 'justify-end' : 'justify-center'}`}>
          <button
            type="button"
            onClick={() => { setExpanded((e) => !e) }}
            title={expanded ? 'Thu gọn' : 'Mở rộng menu'}
            aria-label={expanded ? 'Thu gọn' : 'Mở rộng menu'}
            className="flex items-center justify-center w-7 h-7 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition border border-slate-200"
          >
            {expanded ? <ChevronLeft size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
          </button>
        </div>
      <nav
        aria-label={t('nav.az')}
        className={`overflow-y-auto space-y-0.5 md:space-y-1 ${expanded ? 'p-2' : 'p-2 md:px-2 md:pb-3'}`}
      >
        {/* ⭐ TIÊU ĐỀ NHÓM BẤM ĐƯỢC + mục con thụt trong vạch trái — chép `NavGroup` `Layout.tsx:402-422`
            (soi mobile 12/10: trước là nhãn tĩnh, mục con nằm sát lề nên menu ☰ trông khác hẳn bản cũ).
            Sidebar thu gọn trên desktop thì không có tiêu đề nhóm: mục con hiện thẳng dạng icon. */}
        {/* Hai nút như bản cũ (`Layout.tsx:406` màn hẹp · `:428` desktop rộng) — mỗi nút giữ trạng
            thái riêng của mình (xem `mobileOpen`/`desktopOpen`). */}
        {([['md:hidden', mobileOpen, setMobileOpen], ['hidden md:flex', desktopOpen, setDesktopOpen]] as const).map(([cls, isOpen, setOpen]) => (
          (cls === 'md:hidden' || expanded) && (
            <button
              key={cls}
              type="button"
              aria-expanded={isOpen}
              onClick={() => { setOpen((o) => !o) }}
              className={`w-full ${cls === 'md:hidden' ? 'flex md:hidden' : cls} items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-50 transition`}
            >
              <Package size={20} strokeWidth={1.75} className="shrink-0" />
              {t('nav.az')}
              <span aria-hidden="true" className={`ml-auto text-[10px] text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
            </button>
          )
        ))}
        {/* Thu gọn trên desktop: cả nhóm là MỘT icon + bảng nổi (bản cũ `Layout.tsx:447-500`). */}
        {!expanded && <CompactGroup leaves={leaves} t={t} currentPath={currentPath} onNavigate={go} />}
        <ul
          className={[
            'space-y-0.5 ml-4 pl-2 border-l border-slate-100 mt-0.5',
            // Thu gọn trên desktop thì danh sách nhường chỗ cho icon nhóm ở trên; màn hẹp (drawer) giữ nguyên.
            mobileOpen ? '' : 'max-md:hidden',
            expanded && desktopOpen ? '' : 'md:hidden',
          ].join(' ')}
        >
          {toBlocks(leaves).map((b) =>
            'leaf' in b ? (
              <NavLeaf key={b.leaf.to} item={b.leaf} t={t} currentPath={currentPath} onNavigate={go} expanded={expanded} />
            ) : (
              <NavSection
                key={b.section}
                section={b.section}
                items={b.items}
                t={t}
                currentPath={currentPath}
                onNavigate={go}
                expanded={expanded}
              />
            ),
          )}
        </ul>
        {/* Vạch ngăn trước khối cuối (Cài đặt) — `Layout.tsx:902`. */}
        {bottom.length > 0 && <div className="border-t border-slate-200 mx-1 my-1.5" />}
        <ul className="space-y-0.5">
          {bottom.map((l) => (
            <NavLeaf key={l.to} item={l} t={t} currentPath={currentPath} onNavigate={go} expanded={expanded} />
          ))}
        </ul>
      </nav>
      </aside>
      <main ref={mainRef} className="flex-1 min-w-0 overflow-y-auto bg-slate-50 p-4 md:p-6">{children}</main>
      </div>
    </div>
  )
}
